/**
 * Vendor archive / lookup helpers (mirrors customersRefresh for VendorManagement).
 * Redux-first: boot + Sync list GET; open page reads store; hole-fill GET by id only.
 */

import apiClient from "./apiClient";

const getStore = () => require("../index").default;

const toVendorArray = (data) => {
  if (!data) return [];
  return Array.isArray(data) ? data : [data];
};

/** True when boot/Sync (or prior fetch) already populated vendorsState. */
export const vendorsPresentInStore = (vendorsState) => {
  const state = vendorsState || getStore().getState()?.vendorsState;
  if (!state) return false;
  if (state.vendorsLoaded) return true;
  return (
    (state.vendors || []).length > 0 ||
    (state.active_vendors || []).length > 0 ||
    (state.archived_vendors || []).length > 0
  );
};

/** Re-flatten individuals/organizations from current vendorsState (for PO pickers). */
export const syncVendorPartiesFromStore = () => {
  const store = getStore();
  const list = store.getState()?.vendorsState?.vendors || [];
  const { individuals, organizations } = flattenVendorPartiesFromList(list);
  store.dispatch({ type: "GET_INDIVIDUALS", individuals });
  store.dispatch({ type: "GET_ORGANIZATIONS", organizations });
};

/**
 * Replace vendors from list API → Redux (+ parties). Use for Sync / force refresh.
 */
export const refreshVendorsInStore = async ({
  context = "Loading Vendors",
  silent = true,
} = {}) => {
  const data = await apiClient.get("/VendorManagement/vendors", {
    context,
    silent,
    hideLoader: silent,
  });
  const list = toVendorArray(data);
  applyVendorsWithPartiesToStore(list);
  return list;
};

/**
 * Ensure vendors are in Redux. Skips network when already loaded unless force.
 */
export const ensureVendorsInStore = async ({
  force = false,
  context = "Loading Vendors",
  silent = true,
} = {}) => {
  if (!force && vendorsPresentInStore()) {
    // Parties may be empty if an older path only dispatched GET_VENDORS
    const root = getStore().getState();
    const vendors = root?.vendorsState?.vendors || [];
    const hasParties =
      (root?.individualsState?.individuals || []).length > 0 ||
      (root?.organizationsState?.organizations || []).length > 0 ||
      vendors.length === 0;
    if (!hasParties) syncVendorPartiesFromStore();
    return vendors;
  }
  return refreshVendorsInStore({ context, silent });
};

/** Enough detail to render edit/detail without a GET-by-id. */
export const isVendorDetailUsable = (vendor) => {
  if (!vendor) return false;
  const hasInd = Array.isArray(vendor.individuals) && vendor.individuals.length > 0;
  const hasOrg = Array.isArray(vendor.organizations) && vendor.organizations.length > 0;
  return hasInd || hasOrg;
};

/**
 * Redux-first vendor by id. Network GET /VendorManagement/vendor/{id} only on miss/thin.
 */
export const resolveVendorById = async (
  id,
  { forceNetwork = false, context = "Loading Vendor" } = {}
) => {
  const key = String(id ?? "").trim();
  if (!key || key.toLowerCase() === "null") return null;

  const store = getStore();
  if (!forceNetwork) {
    const fromStore = findVendorInState(store.getState()?.vendorsState, key);
    if (isVendorDetailUsable(fromStore)) return fromStore;
  }

  const raw = await apiClient.get(`/VendorManagement/vendor/${key}`, { context });
  const row = Array.isArray(raw) ? raw[0] : raw;
  if (!row) return null;
  const normalized = {
    ...row,
    POID: row.POID || row.id || row.poid || key,
    id: row.id || row.POID || row.poid || key,
  };
  upsertVendorInStore(normalized);
  return normalized;
};

/** Flat party rows for AddVendor name/email dedupe (from Redux list). */
export const flattenVendorsForDedup = (vendors = []) => {
  const all = [];
  (vendors || []).forEach((v) => {
    const wrapperId = vendorWrapperId(v);
    (v.individuals || []).forEach((i) =>
      all.push({ ...i, type: "Individual", POID: wrapperId || v.POID })
    );
    (v.organizations || []).forEach((o) =>
      all.push({ ...o, type: "Organization", POID: wrapperId || v.POID })
    );
  });
  return all;
};

export const getVendorNestedStatus = (vendor) => {
  // Prefer wrapper status so soft-archive on Vendor row moves list tabs correctly
  const wrapper = vendor?.status;
  if (wrapper != null && String(wrapper).trim() !== "") {
    const w = String(wrapper).trim().toLowerCase();
    if (w === "archived" || w === "archive") return "Archived";
    if (w === "active") return "Active";
  }
  const raw =
    vendor?.organizations?.[0]?.status ||
    vendor?.individuals?.[0]?.status ||
    "Active";
  const nested = String(raw || "Active");
  if (nested.toLowerCase() === "archived" || nested.toLowerCase() === "archive") {
    return "Archived";
  }
  return nested;
};

export const isVendorArchived = (vendor) =>
  getVendorNestedStatus(vendor).toLowerCase() === "archived";

export const partitionVendorsByStatus = (list = []) => {
  const active_vendors = [];
  const archived_vendors = [];
  (list || []).forEach((vendor) => {
    if (isVendorArchived(vendor)) archived_vendors.push(vendor);
    else active_vendors.push(vendor);
  });
  return { active_vendors, archived_vendors };
};

export const vendorWrapperId = (vendor) => {
  if (vendor == null) return "";
  for (const v of [vendor.POID, vendor.id, vendor.poid]) {
    if (v == null) continue;
    const s = String(v).trim();
    if (s && s.toLowerCase() !== "null" && s.toLowerCase() !== "undefined") return s;
  }
  return "";
};

export const findVendorInState = (vendorsState, id) => {
  if (id == null || String(id).trim().toLowerCase() === "null") return null;
  const normalizedId = String(id);
  const pools = [
    ...(vendorsState?.vendors || []),
    ...(vendorsState?.active_vendors || []),
    ...(vendorsState?.archived_vendors || []),
  ];
  const direct = pools.find(
    (vendor) => vendorWrapperId(vendor) === normalizedId
  );
  if (direct) return direct;
  return findVendorWrapperByPartyId(pools, normalizedId);
};

/** Resolve vendor wrapper when PO/API stored organization or individual id by mistake. */
export const findVendorWrapperByPartyId = (vendorsList, partyId) => {
  const pid = String(partyId ?? "").trim();
  if (!pid || pid.toLowerCase() === "null") return null;
  for (const wrapper of vendorsList || []) {
    for (const org of wrapper.organizations || []) {
      if (String(org?.id ?? org?.POID ?? "") === pid) return wrapper;
    }
    for (const ind of wrapper.individuals || []) {
      if (String(ind?.id ?? ind?.POID ?? "") === pid) return wrapper;
    }
  }
  return null;
};

/**
 * Flatten VendorManagement wrappers into selectable party rows.
 * POID / Vendor_Id on each row = vendor wrapper id (not org/individual id).
 */
export const attachVendorIdToParty = (party, vendorWrapper, typeHint) => {
  const vendorId = vendorWrapperId(vendorWrapper);
  const partyId = String(party?.POID ?? party?.id ?? "").trim();
  const typeRaw = typeHint || party?.type || vendorWrapper?.type || "";
  let type = "organization";
  if (typeRaw === "Individual" || typeRaw === "individual") type = "individual";
  else if (typeRaw === "Organization" || typeRaw === "organization") type = "organization";

  return {
    ...party,
    type,
    Vendor_Id: vendorId,
    vendor_Id: vendorId,
    vendorId,
    POID: vendorId,
    partyId,
  };
};

export const flattenVendorPartiesFromList = (vendors = []) => {
  const individuals = [];
  const organizations = [];
  const selectable = [];

  (vendors || []).forEach((wrapper) => {
    const vendorId = vendorWrapperId(wrapper);
    if (!vendorId) return;

    (wrapper.individuals || []).forEach((ind) => {
      const row = attachVendorIdToParty(ind, wrapper, "individual");
      individuals.push(row);
      selectable.push(row);
    });
    (wrapper.organizations || []).forEach((org) => {
      const row = attachVendorIdToParty(org, wrapper, "organization");
      organizations.push(row);
      selectable.push(row);
    });
  });

  return { individuals, organizations, selectable };
};

/** Dispatch GET_VENDORS + flattened individuals/organizations for PO pickers. */
export const applyVendorsWithPartiesToStore = (vendors) => {
  const list = Array.isArray(vendors) ? vendors : [];
  const { individuals, organizations } = flattenVendorPartiesFromList(list);
  const store = getStore();
  store.dispatch({ type: "GET_VENDORS", vendors: list });
  store.dispatch({ type: "GET_INDIVIDUALS", individuals });
  store.dispatch({ type: "GET_ORGANIZATIONS", organizations });
};

const vendorIdOf = (vendor) => vendorWrapperId(vendor);

const withNestedStatus = (vendor, status) => {
  if (!vendor) return vendor;
  const next = { ...vendor, status: status === "Archived" ? "archived" : "active" };
  if (Array.isArray(vendor.organizations) && vendor.organizations.length > 0) {
    next.organizations = vendor.organizations.map((org, idx) =>
      idx === 0 ? { ...org, status } : org
    );
  }
  if (Array.isArray(vendor.individuals) && vendor.individuals.length > 0) {
    next.individuals = vendor.individuals.map((ind, idx) =>
      idx === 0 ? { ...ind, status } : ind
    );
  }
  return next;
};

/**
 * Build VendorManagement PUT body to set Active / Archived while preserving fields.
 */
export const buildVendorStatusPayload = (vendor, status = "Archived") => {
  const isOrg =
    vendor?.type === "Organization" ||
    vendor?.type === "organization" ||
    (Array.isArray(vendor?.organizations) && vendor.organizations.length > 0);

  if (isOrg) {
    const org = vendor.organizations?.[0] || {};
    return {
      status: status === "Archived" ? "archived" : "active",
      type: "Organization",
      organizations: [
        {
          tradingName: org.tradingName || vendor.tradingName || "",
          status,
          contactMediums: org.contactMediums || [
            { mediumCharacteristics: [{}] },
          ],
        },
      ],
    };
  }

  const ind = vendor.individuals?.[0] || {};
  return {
    status: status === "Archived" ? "archived" : "active",
    type: "Individual",
    individuals: [
      {
        gender: ind.gender || "Male",
        placeOfBirth: ind.placeOfBirth || "",
        countryOfBirth: ind.countryOfBirth || "",
        nationality: ind.nationality || "",
        maritalStatus: ind.maritalStatus || "Single",
        title: ind.title || "Mr.",
        givenName: ind.givenName || "",
        familyName: ind.familyName || "",
        middleName: ind.middleName || "",
        location: ind.location || "",
        fullName: ind.fullName || "",
        status,
        contactMediums: ind.contactMediums || [
          { mediumCharacteristics: [{}] },
        ],
      },
    ],
  };
};

export const upsertVendorInStore = (vendor) => {
  if (!vendor) return;
  const store = getStore();
  const id = vendor.POID || vendor.id || vendor.poid;
  if (!id) return;
  const existing = findVendorInState(store.getState()?.vendorsState, id);
  const normalized = {
    ...(existing || {}),
    ...vendor,
    POID: id,
    id: vendor.id || id,
  };
  const archived = isVendorArchived(normalized);
  store.dispatch({
    type: archived ? "UPSERT_ARCHIVED_VENDOR" : "UPSERT_ACTIVE_VENDOR",
    payload: withNestedStatus(
      normalized,
      archived ? "Archived" : getVendorNestedStatus(normalized) || "Active"
    ),
  });
  syncVendorPartiesFromStore();
};

export const applyVendorsToStore = (vendors) => {
  getStore().dispatch({
    type: "GET_VENDORS",
    vendors: Array.isArray(vendors) ? vendors : [],
  });
};

export { withNestedStatus, vendorIdOf };
